import { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/site/Navbar';
import { Footer } from '@/components/site/Footer';
import { WaitlistForm } from '@/components/site/WaitlistForm';
import { WaitlistCounter } from '@/components/site/WaitlistCounter';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { setPageMeta } from '@/lib/seo';
import { useT, useRaw, useLang } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';
import { createCheckoutSession } from '@/lib/api';
import { Check, Sparkles, Loader2, X } from 'lucide-react';

const TIER_COLORS = {
  basique: '#0EA5E9',
  pro: '#8B5CF6',
  entreprise: '#10B981',
};
const TIER_HIGHLIGHT = { pro: true };

export default function Pricing() {
  const t = useT();
  const raw = useRaw();
  const { lang } = useLang();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null); // 'basique' | 'pro' | null
  const [searchParams, setSearchParams] = useSearchParams();
  // Init synchrone (avant tout re-render en StrictMode) : détecte checkout=cancel dans l'URL.
  const [showCancelBanner, setShowCancelBanner] = useState(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('checkout') === 'cancel';
  });

  const tiers = raw('pricing.tiers') || [];
  const faq = raw('pricing.faq') || [];

  useEffect(() => {
    setPageMeta({
      title: t('seo.pricing_title'),
      description: t('seo.pricing_description'),
      path: '/tarifs',
    });
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [t, lang]);

  // Toast + nettoyage URL au premier montage si checkout=cancel présent.
  useEffect(() => {
    if (!showCancelBanner) return;
    toast(t('pricing.checkout_cancel_toast'), { icon: '↩︎', duration: 5000 });
    const next = new URLSearchParams(searchParams);
    if (next.get('checkout') === 'cancel') {
      next.delete('checkout');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCancelBanner]);

  const handleCheckout = async (plan) => {
    if (loadingPlan) return;
    // Pas authentifié → rediriger vers /inscription en préservant plan/interval
    if (!authLoading && !user) {
      const interval = annual ? 'year' : 'month';
      const next = `/tarifs?autopay=1&plan=${encodeURIComponent(plan)}&interval=${encodeURIComponent(interval)}`;
      navigate(`/inscription?next=${encodeURIComponent(next)}`);
      return;
    }
    setLoadingPlan(plan);
    track('checkout_started', { lang, vertical: undefined });
    const res = await createCheckoutSession({
      plan,
      interval: annual ? 'year' : 'month',
      lang,
      user_id: user?.id,
      email: user?.email,
    });
    if (res.ok && res.checkout_url) {
      toast.loading(t('pricing.checkout_loading'), { duration: 4000 });
      window.location.href = res.checkout_url;
      return;
    }
    setLoadingPlan(null);
    toast.error(t('pricing.checkout_error'), { duration: 5000 });
  };

  // Reprise auto du checkout après inscription (?autopay=1&plan=&interval=)
  useEffect(() => {
    if (authLoading) return;
    const auto = searchParams.get('autopay');
    if (auto !== '1') return;
    if (!user) return;
    const p = searchParams.get('plan');
    const inter = searchParams.get('interval');
    if (!p || !inter) return;
    // Nettoyer URL et déclencher
    const next = new URLSearchParams(searchParams);
    next.delete('autopay'); next.delete('plan'); next.delete('interval');
    setSearchParams(next, { replace: true });
    setAnnual(inter === 'year');
    // Petit délai pour que setAnnual soit appliqué
    setTimeout(() => handleCheckout(p), 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  return (
    <div data-testid="page-pricing">
      <Navbar />
      <main>
        {showCancelBanner && (
          <div
            className="fixed left-1/2 top-[92px] z-40 w-[92%] max-w-2xl -translate-x-1/2 md:top-[112px]"
            data-testid="pricing-cancel-banner"
            role="status"
            aria-live="polite"
          >
            <div className="flex items-start gap-3 rounded-2xl border border-[var(--om-border)] bg-[var(--om-bg-pure)] px-4 py-3 shadow-[0_20px_50px_-20px_rgba(10,14,26,0.25)]">
              <span
                className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-bold"
                style={{ background: 'rgba(249,115,22,0.15)', color: '#F97316' }}
                aria-hidden
              >
                ↩︎
              </span>
              <p className="flex-1 text-sm leading-snug text-[var(--om-ink)]">
                {t('pricing.checkout_cancel_banner_title')}{' '}
                <span className="text-[var(--om-muted)]">
                  {t('pricing.checkout_cancel_banner_subtitle')}
                </span>
              </p>
              <button
                type="button"
                onClick={() => setShowCancelBanner(false)}
                data-testid="pricing-cancel-banner-close"
                aria-label={t('pricing.checkout_cancel_banner_close')}
                className="rounded-full p-1 text-[var(--om-muted-2)] transition-colors hover:bg-[var(--om-bg)] hover:text-[var(--om-ink)]"
              >
                <X size={16} strokeWidth={2.4} />
              </button>
            </div>
          </div>
        )}

        <section className="relative overflow-hidden pt-[128px] md:pt-[168px] pb-10">
          <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
            <div
              className="absolute left-1/2 -top-24 h-[420px] w-[420px] -translate-x-1/2 rounded-full blur-3xl"
              style={{ background: 'radial-gradient(closest-side, rgba(139,92,246,0.28), transparent 70%)' }}
            />
          </div>
          <div className="om-container text-center">
            <span className="om-eyebrow">{t('pricing.eyebrow')}</span>
            <h1 className="om-h1 mt-6 text-[44px] sm:text-[56px] lg:text-[64px] text-[var(--om-ink)]">
              {t('pricing.title_l1')}<br />{t('pricing.title_l2')}
            </h1>
            <p className="mx-auto mt-6 max-w-xl text-base md:text-lg leading-relaxed text-[var(--om-muted)]">
              {t('pricing.subtitle')}
            </p>

            <div className="mt-10 inline-flex items-center gap-2 rounded-full border border-[var(--om-border)] bg-[var(--om-bg-pure)]/85 p-1 backdrop-blur">
              <button
                type="button"
                data-testid="pricing-toggle-monthly"
                onClick={() => setAnnual(false)}
                className={`rounded-full px-5 py-2 text-sm font-semibold transition-all ${
                  !annual ? 'bg-[var(--om-ink)] text-[var(--om-btn-primary-fg)] shadow-[0_10px_20px_-14px_rgba(10,14,26,0.5)]' : 'text-[var(--om-muted)]'
                }`}
              >
                {t('pricing.toggle_monthly')}
              </button>
              <button
                type="button"
                data-testid="pricing-toggle-annual"
                onClick={() => setAnnual(true)}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-all ${
                  annual ? 'bg-[var(--om-ink)] text-[var(--om-btn-primary-fg)] shadow-[0_10px_20px_-14px_rgba(10,14,26,0.5)]' : 'text-[var(--om-muted)]'
                }`}
              >
                {t('pricing.toggle_annual')}
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                  annual ? 'bg-emerald-500 text-white' : 'bg-emerald-100 text-emerald-700'
                }`}>{t('pricing.discount')}</span>
              </button>
            </div>
          </div>
        </section>

        <section className="pb-20">
          <div className="om-container">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {tiers.map((tier) => {
                const color = TIER_COLORS[tier.key] || '#0A0E1A';
                const highlighted = !!TIER_HIGHLIGHT[tier.key];
                const priceLabel = annual ? tier.tagAnnual : tier.tag;
                const enterpriseLike = tier.key === 'entreprise';
                const isLoading = loadingPlan === tier.key;
                return (
                  <div
                    key={tier.key}
                    data-testid={`pricing-tier-${tier.key}`}
                    className={`relative flex flex-col rounded-3xl border p-8 ${
                      highlighted
                        ? 'border-transparent bg-[var(--om-bg-pure)] shadow-[0_30px_60px_-30px_rgba(10,14,26,0.3)] ring-1 ring-[var(--om-ink)]/5'
                        : 'border-[var(--om-border)] bg-[var(--om-bg-pure)]/80 shadow-[0_10px_30px_-20px_rgba(10,14,26,0.15)]'
                    }`}
                  >
                    {highlighted && (
                      <span
                        className="absolute -top-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white"
                        style={{ background: color, boxShadow: `0 10px 24px -10px ${color}` }}
                      >
                        <Sparkles size={12} strokeWidth={2.6} /> {t('pricing.popular')}
                      </span>
                    )}

                    <div className="flex items-baseline justify-between">
                      <h3 className="text-xl font-bold text-[var(--om-ink)]">{tier.name}</h3>
                      <span
                        data-testid={`pricing-tier-${tier.key}-price`}
                        className="rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider"
                        style={{ color, background: `${color}18` }}
                      >
                        {priceLabel}
                      </span>
                    </div>

                    <p className="mt-4 text-sm leading-relaxed text-[var(--om-muted)]">
                      {tier.description}
                    </p>

                    <ul className="mt-6 flex-1 space-y-3">
                      {tier.features.map((f, fi) => (
                        <li key={fi} className="flex items-start gap-3 text-sm text-[var(--om-ink-soft)]">
                          <span
                            className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full"
                            style={{ background: `${color}22`, color }}
                          >
                            <Check size={13} strokeWidth={3} />
                          </span>
                          {f}
                        </li>
                      ))}
                    </ul>

                    {enterpriseLike ? (
                      <a
                        href="/contact"
                        className="om-btn-ghost mt-8 w-full"
                        data-testid={`pricing-cta-${tier.key}`}
                      >
                        {tier.cta}
                      </a>
                    ) : (
                      <button
                        type="button"
                        disabled={!!loadingPlan}
                        onClick={() => handleCheckout(tier.checkoutPlan || tier.key)}
                        className={`mt-8 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full text-sm font-semibold transition-all duration-200 ease-out hover:-translate-y-[1px] focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${
                          highlighted
                            ? 'text-white'
                            : 'border border-[var(--om-border)] bg-[var(--om-bg-pure)] text-[var(--om-ink)] hover:shadow-[0_10px_20px_-14px_rgba(10,14,26,0.25)]'
                        }`}
                        style={highlighted ? { background: color, boxShadow: `0 14px 28px -14px ${color}` } : undefined}
                        data-testid={`pricing-cta-${tier.key}`}
                      >
                        {isLoading ? <Loader2 size={16} strokeWidth={2.5} className="animate-spin" /> : null}
                        {isLoading ? t('pricing.checkout_loading') : tier.cta}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="om-container">
            <div className="mx-auto max-w-3xl">
              <div className="text-center">
                <span className="om-eyebrow">{t('pricing.faq_eyebrow')}</span>
                <h2 className="om-h1 mt-5 text-[32px] sm:text-[40px] text-[var(--om-ink)]">
                  {t('pricing.faq_title_l1')}<br />{t('pricing.faq_title_l2')}
                </h2>
              </div>

              <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="pricing-faq">
                {faq.map((f, i) => (
                  <AccordionItem
                    key={i}
                    value={`item-${i}`}
                    className="overflow-hidden rounded-2xl border border-[var(--om-border)] bg-[var(--om-bg-pure)] px-5"
                    data-testid={`faq-item-${i + 1}`}
                  >
                    <AccordionTrigger className="text-left text-base font-semibold text-[var(--om-ink)] hover:no-underline">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-[var(--om-muted)]">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        <section id="pricing-waitlist" className="pb-24 pt-6 md:pb-32">
          <div className="om-container">
            <div className="relative overflow-hidden rounded-[32px] border border-[var(--om-border)] bg-[var(--om-bg-pure)] p-10 shadow-[0_30px_80px_-40px_rgba(10,14,26,0.25)] md:p-14">
              <div className="relative grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
                <div className="lg:col-span-7">
                  <span className="om-eyebrow">{t('pricing.waitlist_eyebrow')}</span>
                  <h2 className="om-h1 mt-4 text-[30px] sm:text-[40px] text-[var(--om-ink)]">
                    {t('pricing.waitlist_title')}
                  </h2>
                  <p className="mt-4 max-w-xl text-base leading-relaxed text-[var(--om-muted)]">
                    {t('pricing.waitlist_subtitle')}
                  </p>
                </div>
                <div className="lg:col-span-5">
                  <WaitlistForm source="pricing" variant="cta" />
                  <div className="mt-4"><WaitlistCounter /></div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
